import React, { useRef, useState, useEffect, useCallback } from 'react';

// Standalone full-screen image zoom overlay.
// Props:
//   open: boolean
//   onClose: () => void
//   images: [{ key, label, src }]  // e.g. [{ key: 'front', label: 'Front', src: '...' }, { key: 'back', ...}]
//   initialKey: key of the image to show when opened (must match an images[].key)
//
// Behavior:
//   - Pinch-to-zoom around the pinch centroid (1×–5×)
//   - Drag-to-pan when zoomed in
//   - Bottom bar: Front | Back | ✕ (swap buttons + red close)
//   - Tap anywhere else does NOT close — only the ✕ button
//
// The swap bar is position:fixed OUTSIDE the transformed image so pinching
// never affects it.

const MIN_SCALE = 1;
const MAX_SCALE = 5;

export default function ImageLightbox({ open, onClose, images, initialKey }) {
    const [activeKey, setActiveKey] = useState(initialKey);
    const imgWrapRef = useRef(null);

    // Transform state
    const scaleRef = useRef(1);
    const txRef    = useRef(0);
    const tyRef    = useRef(0);

    // Pinch anchor
    const pStartDistRef  = useRef(0);
    const pStartScaleRef = useRef(1);
    const pStartTxRef    = useRef(0);
    const pStartTyRef    = useRef(0);
    const pStartMidXRef  = useRef(0);
    const pStartMidYRef  = useRef(0);
    const pStartCXRef    = useRef(0);
    const pStartCYRef    = useRef(0);

    // Pan anchor
    const panningRef      = useRef(false);
    const panStartXRef    = useRef(0);
    const panStartYRef    = useRef(0);
    const panStartTxRef   = useRef(0);
    const panStartTyRef   = useRef(0);

    const applyTransform = useCallback(() => {
        if (!imgWrapRef.current) return;
        imgWrapRef.current.style.transform =
            `translate(${txRef.current}px, ${tyRef.current}px) scale(${scaleRef.current})`;
    }, []);

    const resetTransform = useCallback(() => {
        scaleRef.current = 1;
        txRef.current = 0;
        tyRef.current = 0;
        applyTransform();
    }, [applyTransform]);

    useEffect(() => {
        if (open) {
            setActiveKey(initialKey);
            resetTransform();
        }
    }, [open, initialKey, resetTransform]);

    const imgCenter = () => {
        if (!imgWrapRef.current) return { x: 0, y: 0 };
        const r = imgWrapRef.current.getBoundingClientRect();
        return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
    };

    const midOf = (touches) => {
        if (touches.length === 1) return { x: touches[0].clientX, y: touches[0].clientY };
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2,
        };
    };

    const distOf = (touches) => Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY
    );

    const basePinch = (touches) => {
        pStartDistRef.current  = distOf(touches);
        pStartScaleRef.current = scaleRef.current;
        pStartTxRef.current    = txRef.current;
        pStartTyRef.current    = tyRef.current;
        const m = midOf(touches);
        pStartMidXRef.current = m.x;
        pStartMidYRef.current = m.y;
        const c = imgCenter();
        pStartCXRef.current = c.x;
        pStartCYRef.current = c.y;
    };

    const basePan = (touch) => {
        panningRef.current   = true;
        panStartXRef.current = touch.clientX;
        panStartYRef.current = touch.clientY;
        panStartTxRef.current = txRef.current;
        panStartTyRef.current = tyRef.current;
    };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            basePinch(e.touches);
            panningRef.current = false;
            e.preventDefault();
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            basePan(e.touches[0]);
            e.preventDefault();
        }
    };

    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pStartDistRef.current > 0) {
            const d = distOf(e.touches);
            const m = midOf(e.touches);
            const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE,
                pStartScaleRef.current * (d / pStartDistRef.current)));
            const effRatio = newScale / pStartScaleRef.current;
            txRef.current = pStartTxRef.current + (m.x - pStartMidXRef.current)
                - (effRatio - 1) * (pStartMidXRef.current - pStartCXRef.current);
            tyRef.current = pStartTyRef.current + (m.y - pStartMidYRef.current)
                - (effRatio - 1) * (pStartMidYRef.current - pStartCYRef.current);
            scaleRef.current = newScale;
            applyTransform();
            e.preventDefault();
        } else if (e.touches.length === 1 && panningRef.current) {
            const t = e.touches[0];
            txRef.current = panStartTxRef.current + (t.clientX - panStartXRef.current);
            tyRef.current = panStartTyRef.current + (t.clientY - panStartYRef.current);
            applyTransform();
            e.preventDefault();
        }
    };

    const onTouchEnd = (e) => {
        if (e.touches.length === 2) {
            basePinch(e.touches);
        } else if (e.touches.length === 1 && scaleRef.current > 1) {
            basePan(e.touches[0]);
            pStartDistRef.current = 0;
        } else {
            panningRef.current = false;
            pStartDistRef.current = 0;
        }
        if (scaleRef.current <= 1.001 && e.touches.length === 0) {
            scaleRef.current = 1;
            txRef.current = 0;
            tyRef.current = 0;
            applyTransform();
        }
    };

    // Desktop wheel zoom + mouse drag pan
    const onWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 1.15 : 0.87;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scaleRef.current * delta));
        if (newScale === scaleRef.current) return;
        const c = imgCenter();
        const effRatio = newScale / scaleRef.current;
        txRef.current = txRef.current - (effRatio - 1) * (e.clientX - c.x);
        tyRef.current = tyRef.current - (effRatio - 1) * (e.clientY - c.y);
        scaleRef.current = newScale;
        if (scaleRef.current <= 1.001) {
            scaleRef.current = 1; txRef.current = 0; tyRef.current = 0;
        }
        applyTransform();
    };

    const mouseDownRef = useRef(false);
    const mDragStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
    const onMouseDown = (e) => {
        if (scaleRef.current > 1) {
            mouseDownRef.current = true;
            mDragStartRef.current = { x: e.clientX, y: e.clientY, tx: txRef.current, ty: tyRef.current };
            e.preventDefault();
        }
    };
    useEffect(() => {
        if (!open) return;
        const onMove = (e) => {
            if (mouseDownRef.current && scaleRef.current > 1) {
                txRef.current = mDragStartRef.current.tx + (e.clientX - mDragStartRef.current.x);
                tyRef.current = mDragStartRef.current.ty + (e.clientY - mDragStartRef.current.y);
                applyTransform();
            }
        };
        const onUp = () => { mouseDownRef.current = false; };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [open, applyTransform]);

    if (!open) return null;

    const active = images.find(i => i.key === activeKey) || images[0];

    return (
        <div className="pplb">
            <div className="pplb__hint">Pinch to zoom · Drag to pan</div>
            <div className="pplb__stage">
                <div
                    ref={imgWrapRef}
                    className="pplb__img"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onTouchCancel={onTouchEnd}
                    onWheel={onWheel}
                    onMouseDown={onMouseDown}
                >
                    {active && <img src={active.src} alt={active.label} draggable={false} />}
                </div>
            </div>
            <div className="pplb__swap">
                {images.map(img => (
                    <button
                        key={img.key}
                        className={`pplb__swap-btn${img.key === activeKey ? ' pplb__swap-btn--active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); resetTransform(); setActiveKey(img.key); }}
                    >
                        {img.label}
                    </button>
                ))}
                <button
                    className="pplb__close"
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                >✕</button>
            </div>
        </div>
    );
}
